import { useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import Svg, { Path } from "react-native-svg";
import {
  addLine,
  canAddLine,
  chooseValue,
  choiceOf,
  optionLabelOf,
  removeLine,
  roomFor,
  setLineQuantity,
  valueStates,
  type OptionLine,
  type OptionSet,
} from "@libs/buy-on-behalf-client/options";
import { MEMBER_PRICE, formatMoney, type EventTier } from "./model";
import { LIGHT_TICKET_COLORS as light, QuantityStepper } from "./native";

/**
 * A product chosen by its options, the native twin of the web sheet's
 * lines (`OptionLines` in the purchase sheet): a line per combination, a
 * dropdown per option to the left of its quantity. A dropdown opens its
 * values under its line; one sold out with the line's earlier choices shows
 * disabled. A line that lands on another's combination joins it; Add
 * another takes the next combination on sale while the order has room; any
 * line but the last can be removed. The model is `@libs/buy-on-behalf-client/options`.
 */
export function MerchOptionLines({
  set,
  lines,
  tiers,
  mostPerOrder,
  busy,
  onChange,
}: {
  set: OptionSet;
  lines: OptionLine[];
  /** The ticket types on sale, for each line's price. */
  tiers: EventTier[];
  /** The most units one order holds; null: each line's own limit only. */
  mostPerOrder: number | null;
  busy?: boolean;
  onChange(lines: OptionLine[]): void;
}) {
  // The one dropdown open, by its line and option.
  const [open, setOpen] = useState<{ key: string; option: number } | null>(null);
  return (
    <View style={styles.stack}>
      {lines.map((line, index) => {
        const choice = choiceOf(set, line.values);
        const tier = tiers.find((t) => t.engineItemId === choice?.id);
        const name = line.values.join(", ");
        const shown = open?.key === line.key ? open.option : null;
        const room = roomFor(lines, mostPerOrder, line.key);
        return (
          <View accessibilityLabel={name} key={line.key} style={styles.card}>
            <View style={styles.row}>
              <View style={styles.fields}>
                {set.names.map((_, at) => {
                  // "**T-Shirt (TOUR)** · Size": the item it belongs to, as
                  // every sheet names its rows, then the option.
                  const { part, name: option } = optionLabelOf(set, at);
                  const label = part ? `${part} · ${option}` : option;
                  return (
                  <View key={at} style={styles.field}>
                    <Text numberOfLines={1} style={styles.optionName}>
                      {part ? <Text style={styles.optionPart}>{part}</Text> : null}
                      {part ? ` · ${option}` : option}
                    </Text>
                    <Pressable
                      accessibilityHint={`Chooses the ${option.toLowerCase()}`}
                      accessibilityLabel={`${lines.length > 1 ? `${label}, line ${index + 1}` : label}: ${line.values[at]}`}
                      accessibilityRole="button"
                      accessibilityState={{ disabled: !!busy, expanded: shown === at }}
                      disabled={busy}
                      onPress={() => setOpen(shown === at ? null : { key: line.key, option: at })}
                      style={[styles.select, shown === at && styles.selectOpen]}
                    >
                      <Text numberOfLines={1} style={styles.selectValue}>
                        {line.values[at]}
                      </Text>
                      <Svg height={6} viewBox="0 0 10 6" width={10}>
                        <Path d="M1 1l4 4 4-4" fill="none" stroke={light.secondary} strokeWidth={1.5} />
                      </Svg>
                    </Pressable>
                  </View>
                  );
                })}
              </View>
              <QuantityStepper
                disabled={busy}
                max={Math.min(choice?.maxQuantity ?? line.quantity, room)}
                min={choice?.minQuantity ?? 1}
                onChange={(next) => onChange(setLineQuantity(set, lines, line.key, next, mostPerOrder))}
                value={line.quantity}
                variant="light"
              />
            </View>
            {shown !== null ? (
              <View accessibilityLabel={optionLabelOf(set, shown).name} style={styles.menu}>
                {valueStates(set, line, shown).map((state) => {
                  const chosen = state.value === line.values[shown];
                  return (
                    <Pressable
                      accessibilityLabel={state.available ? state.value : `${state.value}, sold out`}
                      accessibilityRole="button"
                      accessibilityState={{ disabled: !state.available, selected: chosen }}
                      disabled={!state.available || busy}
                      key={state.value}
                      onPress={() => {
                        setOpen(null);
                        onChange(chooseValue(set, lines, line.key, shown, state.value, mostPerOrder));
                      }}
                      style={[styles.value, chosen && styles.valueChosen, !state.available && styles.valueOut]}
                    >
                      <Text style={[styles.valueText, chosen && styles.valueTextChosen, !state.available && styles.valueTextOut]}>
                        {state.value}
                      </Text>
                    </Pressable>
                  );
                })}
              </View>
            ) : null}
            <View style={styles.foot}>
              <Text style={styles.price}>
                {tier ? formatMoney(tier.priceMinor, tier.currency, tier.minorUnits) : ""}
                {/* A fan club member's price: the list price struck through. */}
                {tier?.listPriceMinor !== undefined ? (
                  <>
                    {" "}
                    <Text
                      style={styles.valueTextOut}
                      accessibilityLabel={`was ${formatMoney(tier.listPriceMinor, tier.currency, tier.minorUnits)}`}
                    >
                      {formatMoney(tier.listPriceMinor, tier.currency, tier.minorUnits)}
                    </Text>
                    {` · ${MEMBER_PRICE}`}
                  </>
                ) : null}
              </Text>
              {lines.length > 1 ? (
                <Pressable
                  accessibilityLabel={`Remove ${name}`}
                  accessibilityRole="button"
                  disabled={busy}
                  hitSlop={8}
                  onPress={() => {
                    setOpen(null);
                    onChange(removeLine(lines, line.key));
                  }}
                >
                  <Text style={styles.link}>Remove</Text>
                </Pressable>
              ) : null}
            </View>
          </View>
        );
      })}
      {canAddLine(set, lines, mostPerOrder) ? (
        <Pressable
          accessibilityRole="button"
          disabled={busy}
          hitSlop={8}
          onPress={() => {
            setOpen(null);
            onChange(addLine(set, lines, mostPerOrder));
          }}
          style={styles.add}
        >
          <Text style={styles.link}>Add another</Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  stack: { gap: 8 },
  card: { paddingVertical: 12, paddingHorizontal: 14, borderRadius: 12, backgroundColor: light.card },
  row: { flexDirection: "row", alignItems: "flex-end", justifyContent: "space-between", gap: 12 },
  fields: { flexDirection: "row", flexWrap: "wrap", gap: 8, flexShrink: 1 },
  field: { gap: 3 },
  optionName: { color: light.secondary, fontSize: 12, fontWeight: "500", maxWidth: 200 },
  optionPart: { color: light.text, fontWeight: "700" },
  select: {
    height: 34,
    minWidth: 72,
    maxWidth: 160,
    paddingHorizontal: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: light.border,
    backgroundColor: light.surface,
  },
  selectOpen: { borderColor: light.primary },
  selectValue: { color: light.text, fontSize: 15, fontWeight: "600", flexShrink: 1 },
  menu: { flexDirection: "row", flexWrap: "wrap", gap: 6, marginTop: 10 },
  value: {
    minHeight: 32,
    paddingHorizontal: 12,
    justifyContent: "center",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: light.border,
    backgroundColor: light.surface,
  },
  valueChosen: { borderColor: light.primary, backgroundColor: light.primary },
  valueOut: { opacity: 0.45 },
  valueText: { color: light.text, fontSize: 14, fontWeight: "500" },
  valueTextChosen: { color: light.onPrimary },
  valueTextOut: { textDecorationLine: "line-through" },
  foot: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginTop: 6 },
  price: { color: light.secondary, fontSize: 13 },
  link: { color: light.primary, fontSize: 14, fontWeight: "600" },
  add: { alignSelf: "center", paddingVertical: 6 },
});
